/**
 * FigureDeleteModal — 그림 하나를 지우기 직전에 한 번 멈추는 창.
 *
 * 계약: docs/exam-builder-v2/BE-20260827-figure-editing-apis.md §8
 * 회신: docs/exam-builder-v2/BE-20260829-reply-fe-0828.md §2 Q8
 *
 * ── 왜 window.confirm 을 걷어냈나 (오너 결정 2026-08-29) ──
 * 종전 확인은 편집기 안(ImageComponent)의 `window.confirm` 이었고 문구가
 * 「저장해야 반영되고, 저장 전에는 [편집 취소]로 되돌릴 수 있습니다」였다.
 * 회신 Q8 로 삭제가 **DELETE API 즉시 반영**이 되면서 그 문장이 통째로 거짓이 됐다.
 * 다시 써야 하는데, 이 창이 말해야 하는 것이 세 줄이라 확인창 한 문장으로는 담기지 않는다 —
 *   ⓐ 서버에 즉시 나가고 되돌릴 수 없다   ⓑ 그 field 의 편집본이 폐기된다   ⓒ 뒤 그림 순번이 당겨진다
 * 그래서 서식이 있는 창으로 올렸다. 문구 자체는 figureText.figureDeleteWarnings 가 쥔다(검사 대상).
 *
 * ── 얼굴은 AiRunModal 을 따른다 ──
 * 같은 자리(그림 창 위에 겹쳐 뜨는 확인 창)라 여백·글자 크기·노란 띠·단추 배치를 그대로 옮겼다.
 * 새 값을 만들지 않는다. 다른 것은 둘뿐이다 — 지시 입력칸이 없고, 확정 단추가 danger 다.
 *
 * ── z 값 ──
 * `--z-modal-ai-run`(9999986)을 **같이 쓴다**. 그 층의 정의가 「그림 편집 창 위에 뜨는 창」이고
 * 이 창과 AiRunModal 은 동시에 뜨지 않는다(FigureModal 에서 action 이 갈린다). 층을 하나 더
 * 만들면 src/styles/z-index.css 에 변수를 더해야 하는데, 새 층이 필요한 상황이 아니다.
 * ⚠ 부모(FigureModal)는 이 창이 열려 있는 동안 자기 ESC 를 꺼야 한다 — 공용 Modal 이 document 에
 * 전역 ESC 를 달아, 안 끄면 ESC 한 번에 둘 다 닫힌다(AiRunModal 과 같은 규칙).
 */
import { Modal, Button } from '../common';
import { figureDeleteSummary, figureDeleteWarnings } from './figureText';

export default function FigureDeleteModal({
  // target — 되살릴 길이 있는지가 대상마다 다르다(문제은행 본문 그림만 [수정 이력]으로 되돌아온다).
  // 문구가 그 사실을 갈라 말해야 해서 여기까지 나른다 → figureText.figureDeleteWarnings
  isOpen, target, field, lexicalEdited = false,
  // 이 삭제가 편집본을 버리게 되는가(계약 §10 · 2026-09-04). 서버는 이제 편집본을 폐기하지 않고
  // 같은 순번 노드만 빼는데, 순번 대조가 안 될 때만 종전대로 폐기한다. **모르면 undefined** 로
  // 두어 그 줄을 아예 내지 않는다 — 편집본이 있다는 이유만으로 겁을 주면 사람이 지우기를 피한다.
  editedAtRisk,
  busy = false, onConfirm, onClose,
}) {
  const warnings = figureDeleteWarnings({ field, lexicalEdited, target, editedAtRisk });

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => { if (!busy) onClose(); }}
      title="그림 삭제"
      size="md"
      /* 보내는 중에는 닫히지 않는다 — 응답을 못 받고 닫히면 화면이 무슨 일이 났는지 말할 수 없다 */
      closeOnBackdropClick={false}
      closeOnEsc={!busy}
      showCloseButton={!busy}
      zIndex={9999986}
    >
      <div className="space-y-3">
        <p className="text-sm leading-relaxed text-gray-700">{figureDeleteSummary(field)}</p>
        <ul className="list-disc space-y-1.5 rounded-xl border border-amber-200 bg-amber-50 py-3 pl-8 pr-3 text-xs leading-relaxed text-amber-800">
          {warnings.map((t) => <li key={t.slice(0, 16)}>{t}</li>)}
        </ul>
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="secondary" onClick={onClose} disabled={busy}>취소</Button>
          <Button size="sm" variant="danger" onClick={onConfirm} loading={busy} disabled={busy}>
            지웁니다
          </Button>
        </div>
      </div>
    </Modal>
  );
}
